import {
  kbArticleListResponseSchema,
  kbArticleSchema,
  kbArticleVersionListSchema,
  kbCategorySchema,
  kbHelpfulResponseSchema,
  kbLookupsSchema,
  kbSearchResponseSchema,
  kbSuggestResponseSchema,
  type CreateKbArticleRequest,
  type CreateKbCategoryRequest,
  type KbArticleListQuery,
  type KbSearchQuery,
  type KbSuggestQuery,
  type LinkKbTicketRequest,
  type UpdateKbArticleRequest,
} from '@gb-ticket/shared-types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiFetch } from '@/lib/api';
import { toSearchParams } from '@/lib/format';

export const kbLookupsQueryKey = ['kb', 'lookups'] as const;
export const kbArticlesQueryKey = ['kb', 'articles'] as const;

export function kbArticleQueryKey(ref: string) {
  return ['kb', 'articles', ref] as const;
}

export function kbVersionsQueryKey(ref: string) {
  return ['kb', 'articles', ref, 'versions'] as const;
}

export function kbSuggestQueryKey(query: KbSuggestQuery) {
  return ['kb', 'suggest', query] as const;
}

export function kbSearchQueryKey(query: KbSearchQuery) {
  return ['kb', 'search', query] as const;
}

export function useKbLookups() {
  return useQuery({
    queryKey: kbLookupsQueryKey,
    queryFn: () => apiFetch('/kb/lookups', kbLookupsSchema),
  });
}

export function useKbArticles(query: KbArticleListQuery, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: [...kbArticlesQueryKey, query],
    enabled: options?.enabled ?? true,
    queryFn: () =>
      apiFetch(
        `/kb/articles${toSearchParams(query as Record<string, string | number | boolean | undefined>)}`,
        kbArticleListResponseSchema,
      ),
  });
}

export function useKbArticle(ref: string | undefined) {
  return useQuery({
    queryKey: kbArticleQueryKey(ref ?? ''),
    enabled: Boolean(ref),
    queryFn: () => apiFetch(`/kb/articles/${ref}`, kbArticleSchema),
  });
}

export function useKbVersions(ref: string | undefined) {
  return useQuery({
    queryKey: kbVersionsQueryKey(ref ?? ''),
    enabled: Boolean(ref),
    queryFn: () => apiFetch(`/kb/articles/${ref}/versions`, kbArticleVersionListSchema),
  });
}

export function useKbSearch(query: KbSearchQuery | null) {
  return useQuery({
    queryKey: query ? kbSearchQueryKey(query) : ['kb', 'search', 'idle'],
    enabled: Boolean(query?.q && query.q.trim().length > 0),
    queryFn: () =>
      apiFetch('/kb/search', kbSearchResponseSchema, {
        method: 'POST',
        body: JSON.stringify(query),
      }),
  });
}

export function useKbSuggest(query: KbSuggestQuery | null) {
  return useQuery({
    queryKey: query ? kbSuggestQueryKey(query) : ['kb', 'suggest', 'idle'],
    enabled: Boolean(query?.q && query.q.trim().length > 0),
    placeholderData: (previous) => previous,
    queryFn: () =>
      apiFetch('/kb/suggest', kbSuggestResponseSchema, {
        method: 'POST',
        body: JSON.stringify(query),
      }),
  });
}

export function useMarkKbHelpful() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (ref: string) =>
      apiFetch(`/kb/articles/${ref}/helpful`, kbHelpfulResponseSchema, { method: 'POST' }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['kb'] });
    },
  });
}

export function useCreateKbArticle() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateKbArticleRequest) =>
      apiFetch('/kb/articles', kbArticleSchema, { method: 'POST', body: JSON.stringify(body) }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: kbArticlesQueryKey });
    },
  });
}

export function useDraftKbArticleFromTicket() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { ticketNumber: number }) =>
      apiFetch('/kb/articles/draft-from-ticket', kbArticleSchema, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: kbArticlesQueryKey });
    },
  });
}

export function useUpdateKbArticle(ref: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateKbArticleRequest) =>
      apiFetch(`/kb/articles/${ref}`, kbArticleSchema, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    onSuccess: async (article) => {
      await queryClient.invalidateQueries({ queryKey: kbArticlesQueryKey });
      await queryClient.invalidateQueries({ queryKey: kbArticleQueryKey(ref) });
      await queryClient.invalidateQueries({ queryKey: kbArticleQueryKey(article.slug) });
      await queryClient.invalidateQueries({ queryKey: kbVersionsQueryKey(ref) });
    },
  });
}

export function usePublishKbArticle(ref: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => apiFetch(`/kb/articles/${ref}/publish`, kbArticleSchema, { method: 'POST' }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['kb'] });
    },
  });
}

export function useArchiveKbArticle(ref: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => apiFetch(`/kb/articles/${ref}/archive`, kbArticleSchema, { method: 'POST' }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['kb'] });
    },
  });
}

export function useRestoreKbVersion(ref: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (version: number) =>
      apiFetch(`/kb/articles/${ref}/versions/${version}/restore`, kbArticleSchema, {
        method: 'POST',
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['kb'] });
    },
  });
}

export function useLinkKbTicket(ref: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: LinkKbTicketRequest) =>
      apiFetch(`/kb/articles/${ref}/tickets`, kbArticleSchema, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['kb'] });
    },
  });
}

export function useUnlinkKbTicket(ref: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (ticketId: string) =>
      apiFetch(`/kb/articles/${ref}/tickets/${ticketId}`, kbArticleSchema, { method: 'DELETE' }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['kb'] });
    },
  });
}

export function useCreateKbCategory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateKbCategoryRequest) =>
      apiFetch('/kb/categories', kbCategorySchema, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: kbLookupsQueryKey });
    },
  });
}
